/* LuzLed category and search pages: filters and sorting in the browser, from each card's luz.facets
   values. On the search page (data-lc-search) the cards come from the instant search index and the
   query is one more filter, updated as you type. */
(async () => {
  const root = document.querySelector("[data-lc]");
  const grid = root?.querySelector("[data-lc-grid]");
  if (!grid) return;
  const $ = (s, el = root) => el.querySelector(s);
  const $$ = (s, el = root) => [...el.querySelectorAll(s)];
  const esc = (t) => String(t ?? "").replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]);
  const searchMode = "lcSearch" in root.dataset;

  const bucket = (ranges) => (vals) => ranges.filter(([lo, hi]) => vals.some((v) => v > lo && v <= hi)).map((r) => r[2]);
  const power = bucket([[-1, 5, "Up to 5 W"], [5, 10, "5 to 10 W"], [10, 20, "10 to 20 W"], [20, 50, "20 to 50 W"], [50, 100, "50 to 100 W"], [100, 1e6, "Over 100 W"]]);
  const beamB = [[-1, 30, "Spot, up to 30°"], [30, 60, "Medium, 30 to 60°"], [60, 1e3, "Wide, over 60°"]];
  const FIN = ["Black", "White", "Grey", "Silver", "Brass", "Clear", "Orange"];
  /* Lengths read as written ("300 mm", "1.5 m"), sorted by their size. */
  const mm = (v) => parseFloat(v) * (/ m$/.test(v) ? 1000 : 1);
  const list = (key) => (f) => f[key] || [];
  const GROUPS = [
    /* Type: only on pages where every product has one (a system, the profiles, the spare parts), request 144. */
    { key: "type", label: "Type", of: list("type"), all: true,
      order: ["Spots", "Linear lights", "Pendants", "Tracks", "Controllers", "Remotes", "Repeaters", "Power supplies", "Drivers",
        "LED strip", "Profiles", "Profile lights", "Sensors", "Wiring and control", "Connectors", "End caps", "Fixings", "Display boards",
        "LED boards", "Glass and covers", "Bodies", "Batteries", "Solar panels", "Switches", "Lamp holders"] },
    { key: "cct", label: "Colour temperature", of: (f) => [...(f.k || []).map((k) => `${k}K`), ...(f.sel ? ["Selectable"] : [])],
      sort: (a, b) => (parseInt(a) || 1e5) - (parseInt(b) || 1e5) },
    { key: "lc", label: "Light colour", of: (f) => f.lc || [], order: ["Red", "Green", "Blue", "Purple", "Yellow", "Warm white", "RGB"] },
    { key: "power", label: "Power", of: (f) => (f.w ? power(f.w) : []), order: power([0.1, 6, 11, 21, 51, 101]) },
    { key: "volt", label: "Voltage", of: list("volt"), order: ["5V DC", "12V DC", "24V DC", "36V DC", "48V DC", "Mains", "Constant current"] },
    { key: "ctl", label: "Control", of: list("ctl"),
      order: ["RF remote", "Push switch", "Rotary knob", "App", "Sensor", "DALI", "0/1-10V", "Triac dimmer", "PWM signal"] },
    { key: "colour", label: "Colour type", of: list("colour"), order: ["Single colour", "Tunable white", "RGB", "RGBW", "RGB+CCT"] },
    { key: "base", label: "Base", of: (f) => (f.base ? [f.base] : []), sort: (a, b) => a.localeCompare(b, "en", { numeric: true }) },
    { key: "shape", label: "Shape", of: list("shape"),
      order: ["Round", "Square", "Linear", "Classic", "Globe", "Candle", "Edison", "Tube", "Reflector", "Spot", "Capsule",
        "Decorative", "Cylinder", "Long neck"] },
    { key: "glass", label: "Glass", of: list("glass"), order: ["Clear", "Gold", "Amber", "Smoky", "Milky", "Coloured", "Mirror top"] },
    { key: "cut", label: "Cut out", of: list("cut"),
      order: ["Up to 50 mm", "51 to 80 mm", "81 to 120 mm", "121 to 170 mm", "171 to 220 mm", "Linear slot"] },
    { key: "size", label: "Size", of: list("size"), order: ["300 x 600 mm", "600 x 600 mm", "300 x 1200 mm", "600 x 1200 mm"] },
    { key: "len", label: "Length", of: list("len"), sort: (a, b) => mm(a) - mm(b) },
    { key: "mount", label: "Mounting", of: list("mount"), order: ["Surface", "Recessed", "Plaster in", "Suspended", "Corner", "Between walls"] },
    { key: "beam", label: "Beam", of: (f) => (f.beam ? beamB.filter(([lo, hi]) => f.beam[1] > lo && f.beam[0] <= hi).map((r) => r[2]) : []),
      order: beamB.map((r) => r[2]) },
    { key: "ip", label: "IP rating", of: (f) => (f.ip ? [f.ip] : []), sort: (a, b) => a.localeCompare(b) },
    { key: "finish", label: "Finish", of: (f) => f.fin || [], sort: (a, b) => (FIN.indexOf(a) + 1 || 99) - (FIN.indexOf(b) + 1 || 99) },
    { key: "cri", label: "CRI", of: (f) => (f.cri ? [`CRI ${f.cri}+`] : []), sort: (a, b) => b.localeCompare(a) },
    { key: "dim", label: "Dimming", of: (f) => (f.dim === true ? ["Dimmable"] : f.dim === false ? ["Not dimmable"] : []), order: ["Dimmable", "Not dimmable"] },
  ];

  /* Card tags: what each value is, from its form, for the icon beside it; colour temperatures get a dot in the
     colour of the light (same formula as the product page). Mirrors snippets/luz-chip.liquid (request 119). */
  const k2rgb = (k) => {
    const t = k / 100;
    const r = t <= 66 ? 255 : 329.698727446 * Math.pow(t - 60, -0.1332047592);
    const g = t <= 66 ? 99.4708025861 * Math.log(t) - 161.1195681661 : 288.1221695283 * Math.pow(t - 60, -0.0755148492);
    const b = t >= 66 ? 255 : t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
    const c = (v) => Math.round(Math.min(255, Math.max(0, v)));
    return `rgb(${c(r)},${c(g)},${c(b)})`;
  };
  const LC = { Red: "#e8352b", Green: "#22b14c", Blue: "#2f6be0", Purple: "#8b3fd9", Yellow: "#f5c400" };
  const RAINBOW = "linear-gradient(90deg,#e8352b,#f5c400,#22b14c,#2f6be0,#8b3fd9)";
  const chipKind = (t) => /CCT|^\d[\d /]*K$/.test(t) || LC[t.replace(/ light$/, "")] && / light$/.test(t) || t === "RGB" || t === "Light colour choice" ? "k" : / W(\/m)?$/.test(t) ? "w" : /°$/.test(t) ? "beam" : /^IP\d/.test(t) ? "ip"
    : /^CRI/.test(t) ? "cri" : /V (DC|AC)$/.test(t) ? "v" : / m{1,2}$/.test(t) ? "len" : /^(E|B|G|GU)\d/.test(t) ? "base" : / A$/.test(t) ? "a"
    : /RF|DALI|PWM|Motion|Rotary|push|10V/.test(t) ? "ctl" : /zone|channel/.test(t) ? "ch" : "x";
  const dot = (t) => {
    if (/ light$/.test(t) && LC[t.replace(/ light$/, "")]) return LC[t.replace(/ light$/, "")];
    if (t === "RGB" || t === "Light colour choice") return RAINBOW;
    if (/CCT/.test(t)) return `linear-gradient(90deg,${k2rgb(2700)},${k2rgb(6500)})`;
    const ks = t.replace("K", "").split("/").map((x) => parseInt(x));
    if (ks.length === 1) return k2rgb(ks[0]);
    return `linear-gradient(90deg,${ks.map((k, n) => `${k2rgb(k)} ${(n * 100) / ks.length}% ${((n + 1) * 100) / ks.length}%`).join(",")})`;
  };
  const chipHTML = (t) => { const k = chipKind(t); return `<li data-k="${k}">${k === "k" ? `<i style="background:${dot(t)}"></i>` : ""}${esc(t)}</li>`; };

  /* On the search page, build every card from the index once; typing then only shows and hides them. */
  let index = null;
  if (searchMode) {
    if (!window.LuzSearch) return;
    index = await window.LuzSearch.load();
    const FILL = { white: "#f4f4f1", black: "#1b1d1c", grey: "#8a8f8c", "dark grey": "#4a4e4c", orange: "#e9782d", red: "#d8392b",
      green: "#34a853", blue: "#2f6fd6", yellow: "#f2c230", "3000k white": "#ffdca6" };
    grid.innerHTML = index.p.map((e, n) => {
      const name = e.s && e.n.startsWith(e.s + " ") ? e.n.slice(e.s.length + 1) : e.n;
      const img = e.i ? `<img src="${esc(e.i.replace("width=240", "width=600"))}" alt="${esc(name)}" loading="lazy" crossorigin="anonymous">`
        : `<span class="lc-noimg" aria-hidden="true"><svg viewBox="0 0 48 48"><path d="M24 6v6M9.5 12.5l4 4M38.5 12.5l-4 4M16 30a8 8 0 1 1 16 0c0 3-2 4.5-3 6.5h-10c-1-2-3-3.5-3-6.5ZM19 41h10"/></svg></span>`;
      const sws = (e.o || []).length > 1 ? `<span class="lc-sws">${e.o.map((o) => `<i style="background:${FILL[o.toLowerCase()] || "#d6d8d4"}" title="${esc(o)}"></i>`).join("")}</span>` : "";
      const chips = (e.f?.chips || []).length ? `<ul class="lc-chips">${e.f.chips.map(chipHTML).join("")}</ul>` : "";
      /* The + (request 169, luz-quick-add.js): the versions as in snippets/luz-grid-card.liquid, from the index's codes. */
      const vers = (e.k || []).map((k) => [k[0], k[2] || "", k[3]]).filter((k) => k[2]);
      const qa = vers.length ? `<button type="button" class="lc-add" data-qa="${esc(JSON.stringify(vers))}" data-qa-name="${esc(name)}" data-qa-img="${esc(e.i || "")}" ` +
        (vers.length > 1 ? `aria-haspopup="dialog" aria-expanded="false" aria-label="Choose a version of ${esc(name)} to add to the quote list"` : `aria-label="Add ${esc(name)} to the quote list"`) +
        `><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>` : "";
      return `<article class="lc-card" data-lc-card data-n="${n}" data-i="${n}" data-name="${esc(name)}" data-code="${esc(e.c)}" data-w="${e.f?.w?.[0] || 0}" hidden>
        <a class="lc-link" href="${e.u}"><div class="lc-img">${img}${e.v > 1 ? `<span class="lc-ver">${e.v} versions</span>` : ""}</div>
        <div class="lc-body">${e.s ? `<span class="lc-sys">${esc(e.s)}</span>` : ""}<h3 class="lc-t">${esc(name)}</h3><div class="lc-c">${esc(e.c)}</div>
        ${chips || sws ? `<div class="lc-foot">${chips}${sws}</div>` : ""}</div></a>${qa}</article>`;
    }).join("");
  }

  /* A category with more than 250 products (Shopify's page size): its other pages are loaded and added to the grid
     first, so the count, filters and sort cover every product (request 139). The page links stay for no script. */
  const pages = +grid.dataset.lcPages || 1;
  if (!searchMode && pages > 1 && +grid.dataset.lcPage === 1) {
    const extra = await Promise.all(Array.from({ length: pages - 1 }, (_, n) =>
      fetch(`${grid.dataset.lcUrl}?view=cards&page=${n + 2}`, { credentials: "same-origin" }).then((r) => (r.ok ? r.text() : ""))
        .catch(() => "")));
    if (extra.every(Boolean)) {
      extra.forEach((html) => grid.insertAdjacentHTML("beforeend", html));
      $(".lc-pages")?.setAttribute("hidden", "");
    } else $(".lc-pages")?.classList.add("show");
  }

  const cards = $$("[data-lc-card]", grid).map((el) => {
    let f = {};
    const e = searchMode ? index.p[+el.dataset.n] : null;
    if (e) f = e.f || {};
    else try { f = JSON.parse(el.dataset.f) || {}; } catch {}
    const vals = Object.fromEntries(GROUPS.map((g) => [g.key, new Set(g.of(f))]));
    return { el, e, vals, part: f.part ? 1 : 0, noimg: el.querySelector(".lc-noimg") ? 1 : 0, i: +el.dataset.i, name: el.dataset.name, code: el.dataset.code, w: +el.dataset.w || 0,
      link: el.querySelector(".lc-link"), codeEl: el.querySelector(".lc-c"), rank: 0 };
  });

  /* Which filters this page offers: a group with at least two values on at least two products (one value would only
     drop the products that do not state it, read as "the others are not"; request 144); Type only when every product
     on the page has one. */
  const groups = GROUPS.map((g) => {
    const counts = new Map();
    cards.forEach((c) => c.vals[g.key].forEach((v) => counts.set(v, (counts.get(v) || 0) + 1)));
    let opts = [...counts.keys()];
    opts = g.order ? g.order.filter((o) => counts.has(o)) : opts.sort(g.sort);
    return { ...g, opts };
  }).filter((g) => g.opts.length > 1 && cards.filter((c) => c.vals[g.key].size).length > 1 && (!g.all || cards.every((c) => c.vals[g.key].size)));

  const state = Object.fromEntries(groups.map((g) => [g.key, new Set()]));
  const params = new URLSearchParams(location.search);
  groups.forEach((g) => (params.get(g.key) || "").split(",").filter((v) => g.opts.includes(v)).forEach((v) => state[g.key].add(v)));

  /* The search query (search page only). */
  const qInput = $("[data-lc-q]");
  let q = searchMode ? (params.get("q") || "").trim() : "";
  if (qInput) qInput.value = q;
  let found = null;
  const runQuery = () => {
    if (!searchMode) return;
    found = new Map();
    const r = q ? window.LuzSearch.query(q) : { p: [], c: [], g: [] };
    r.p.forEach((x, n) => found.set(x.e, { rank: n, item: x.item }));
    cards.forEach((c) => {
      const hitc = found.get(c.e);
      c.rank = hitc ? hitc.rank : 1e6;
      const item = hitc?.item;
      c.link.href = item ? item[1] : c.e.u;
      c.codeEl.textContent = item ? item[0] : c.e.c;
    });
    const extra = $("[data-ls-extra]");
    if (extra) {
      const links = [...r.c.map((c) => `<a class="lc-cat" href="${c.u}">${esc(c.t)}${c.p ? `<small>${esc(c.p)}</small>` : ""}</a>`),
        ...r.g.map((g) => `<a class="lc-cat" href="${g.u}">${esc(g.t)}</a>`)];
      extra.innerHTML = links.length ? `<span class="ls-extra-l">Also found</span>${links.join("")}` : "";
      extra.hidden = !links.length;
    }
    $("[data-ls-start]").hidden = !!q;
    $("[data-ls-results]").hidden = !q;
    const s = $("[data-ls-sum]");
    if (s) s.innerHTML = q ? `Results for <strong>${esc(q)}</strong>` : "";
  };

  const matches = (c, skip) => (!searchMode || (q && found.has(c.e))) &&
    groups.every((g) => g.key === skip || !state[g.key].size || [...state[g.key]].some((v) => c.vals[g.key].has(v)));
  const nSel = () => groups.reduce((n, g) => n + state[g.key].size, 0);

  /* The filters are built once and then only updated: every option stays in place, and one that would leave no
     product is greyed out instead of removed, so nothing moves under the pointer (request 117; the practice of
     keeping filter layouts stable rather than hiding values). */
  const optHTML = (g) => g.opts.map((o) => `<label class="lc-o"><input type="checkbox" data-g="${g.key}" value="${esc(o)}">` +
    `<span class="lc-box" aria-hidden="true"></span><span class="lc-o-t">${esc(o)}</span><small></small></label>`).join("");
  const chev = `<svg viewBox="0 0 12 8" aria-hidden="true"><path d="M1 1.5 6 6.5 11 1.5"/></svg>`;
  const filters = $("[data-lc-filters]");
  const sheetBody = $("[data-lc-sheet-body]");
  const side = $("[data-lc-side]");
  const openBtn = $("[data-lc-open]");
  filters.innerHTML = groups.map((g) => `<div class="lc-f" data-key="${g.key}"><button type="button" class="lc-pill" aria-expanded="false" data-lc-pill="${g.key}">` +
    `${esc(g.label)}<b hidden></b>${chev}</button><div class="lc-pop" role="group" aria-label="${esc(g.label)}">${optHTML(g)}</div></div>`).join("");
  sheetBody.innerHTML = groups.map((g) => `<fieldset class="lc-sg" data-key="${g.key}"><legend>${esc(g.label)}</legend>${optHTML(g)}</fieldset>`).join("");
  /* Wide screens: the same groups in a sidebar, each folding open or shut (request 103). */
  if (side) {
    side.innerHTML = groups.length ? `<div class="lc-side-h"><h2>Filters</h2><button type="button" class="lc-clear" data-lc-clear>Clear all</button></div>` +
      groups.map((g) => `<details class="lc-sd" data-key="${g.key}" open><summary>${esc(g.label)}<b hidden></b>${chev}</summary><div class="lc-sd-b">${optHTML(g)}</div></details>`).join("") : "";
    root.classList.toggle("lc-noside", !groups.length);
  }
  if (openBtn) openBtn.hidden = !groups.length;

  const setUrl = () => {
    const u = new URLSearchParams(location.search);
    groups.forEach((g) => (state[g.key].size ? u.set(g.key, [...state[g.key]].join(",")) : u.delete(g.key)));
    if (searchMode) q ? u.set("q", q) : u.delete("q");
    const s = u.toString();
    history.replaceState(null, "", location.pathname + (s ? `?${s}` : "") + location.hash);
  };
  /* Batches (request 167; Baymard's product list research: 10 to 30 products first, more loaded as the list is
     scrolled up to 50 to 100, then a Show more button so the footer stays in reach). About 24 cards, whole rows; up to
     72 load on scroll (phones: 24), then the button once: after it the rest load on scroll, also after a new sort or
     filter (request 168, Simon: one button, not one every time; the menu at the top carries the footer's main links). A
     new sort or filter starts again from the first batch. */
  const more = $("[data-lc-more]");
  const moreN = $("[data-lc-more-n]");
  const moreBtn = $("[data-lc-more-btn]");
  const phone = matchMedia("(max-width: 749px)");
  const batch = () => { const c = getComputedStyle(grid).gridTemplateColumns.split(" ").length || 1; return c * Math.ceil(24 / c); };
  let order = cards, hits = new Set(cards), limit = 0, auto = 0, endless = false;
  const reset = () => { limit = batch(); auto = endless ? Infinity : phone.matches ? limit : limit + 2 * batch(); };
  const show = (grow) => {
    const upto = hits.size - limit < batch() / 2 ? hits.size : limit;  // a short rest comes with the batch, never a button for it
    let k = 0;
    order.forEach((c) => {
      const on = hits.has(c) && k++ < upto;
      if (on && grow && c.el.hidden) c.el.classList.add("lc-new");
      else if (!grow) c.el.classList.remove("lc-new");
      c.el.hidden = !on;
    });
    grid.classList.add("lc-on");
    if (!more) return;
    more.hidden = hits.size <= upto;
    moreN.textContent = `Showing ${upto} of ${hits.size}`;
    moreBtn.hidden = endless;
  };
  const grow = (click) => {
    if (!more || more.hidden || (!click && limit >= auto)) return;
    limit += batch();
    if (click) { endless = true; auto = Infinity; }
    show(true);
    if (!click) requestAnimationFrame(() => { near.unobserve(more); near.observe(more); });  // still near the end: once more
  };
  const near = new IntersectionObserver(([e]) => { if (e.isIntersecting) grow(false); }, { rootMargin: "0px 0px 700px 0px" });
  if (more) {
    near.observe(more);
    moreBtn.addEventListener("click", () => grow(true));
  }
  grid.addEventListener("animationend", (e) => e.target.classList.remove("lc-new"));

  /* Search: only the filter groups the results can use show (at least two values on at least two of them, the rule of
     the category pages) or one already chosen; the page builds every group of the catalogue (audit 2026-10-11). */
  const fitGroups = () => {
    if (!searchMode) return;
    const pool = cards.filter((c) => q && found.has(c.e));
    groups.forEach((g) => {
      const vals = new Set(); let n = 0;
      pool.forEach((c) => { if (c.vals[g.key].size) { n++; c.vals[g.key].forEach((v) => vals.add(v)); } });
      const on = state[g.key].size > 0 || (vals.size > 1 && n > 1 && (!g.all || n === pool.length));
      $$(`[data-key="${g.key}"]`).forEach((el) => (el.hidden = !on));
    });
  };

  const render = () => {
    fitGroups();
    const counts = Object.fromEntries(groups.map((g) => {
      const pool = cards.filter((c) => matches(c, g.key));
      return [g.key, new Map(g.opts.map((o) => [o, pool.filter((c) => c.vals[g.key].has(o)).length]))];
    }));
    $$("input[data-g]").forEach((i) => {
      const on = state[i.dataset.g].has(i.value);
      const n = counts[i.dataset.g].get(i.value);
      i.checked = on;
      i.disabled = !n && !on;
      const l = i.closest(".lc-o");
      l.classList.toggle("off", i.disabled);
      l.querySelector("small").textContent = n;
    });
    groups.forEach((g) => {
      const n = state[g.key].size;
      $$(`[data-key="${g.key}"] b`).forEach((b) => { b.hidden = !n; b.textContent = n; });
      $(`[data-lc-pill="${g.key}"]`)?.classList.toggle("on", !!n);
    });
    const k = nSel();
    const sideClear = side && $("[data-lc-clear]", side);
    if (sideClear) sideClear.style.visibility = k ? "" : "hidden";
    hits = new Set(cards.filter((c) => matches(c)));
    show();
    const n = hits.size;
    const countEl = $("[data-lc-count]");
    countEl.textContent = `${n} ${n === 1 ? "product" : "products"}${countEl.dataset.suffix || ""}`;
    $("[data-lc-none]").hidden = n > 0 || (searchMode && !q);
    const badge = $("[data-lc-nsel]");
    badge.hidden = !k;
    badge.textContent = k;
    $("[data-lc-show]").textContent = n ? `Show ${n} ${n === 1 ? "product" : "products"}` : "No products";
    $("[data-lc-active]").innerHTML = groups.flatMap((g) => [...state[g.key]].map((v) =>
      `<button type="button" class="lc-tag" data-g="${g.key}" data-v="${esc(v)}" aria-label="Remove ${esc(v)}">${esc(v)}<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17"/></svg></button>`))
      .join("") + (k ? `<button type="button" class="lc-clear" data-lc-clear>Clear all</button>` : "");
    setUrl();
  };

  /* Search page: one panel of pills open at a time; it opens inward when it would pass the screen edge (request 107). */
  let open = null;
  const setOpen = (key) => {
    open = key;
    $$(".lc-f", filters).forEach((f) => {
      const on = f.dataset.key === key;
      const pop = f.querySelector(".lc-pop");
      f.classList.toggle("open", on);
      f.querySelector(".lc-pill").setAttribute("aria-expanded", on);
      pop.classList.remove("end");
      if (on && pop.getBoundingClientRect().right > document.documentElement.clientWidth - 12) pop.classList.add("end");
    });
  };

  /* A choice while the results are scrolled past brings their top back into view: with the sidebar, under the menu
     where the sidebar holds, so neither the sidebar nor the pointer moves when the list gets shorter (request 154); at
     every width since the list starts again from its first batch (request 166). */
  const keep = () => {
    /* under the sidebar's place, else under whatever sticks at the top (the menu, or the floating bar in its place) */
    const below = side?.offsetParent ? parseFloat(getComputedStyle(side).top)
      : Math.max($("[data-lc-bar]")?.offsetHeight || 0, document.querySelector(".lhd-wrap")?.offsetHeight || 0) + 12;
    const y = ($(".lc-res", root) || grid).getBoundingClientRect().top - below;
    if (y < 0) scrollTo({ top: scrollY + y, behavior: "instant" });
  };
  const toggle = (key, v, on) => { on ? state[key].add(v) : state[key].delete(v); reset(); render(); keep(); };
  const clear = () => { groups.forEach((g) => state[g.key].clear()); reset(); render(); keep(); };

  root.addEventListener("change", (e) => {
    const i = e.target.closest("input[data-g]");
    if (i) toggle(i.dataset.g, i.value, i.checked);
  });
  root.addEventListener("click", (e) => {
    const pill = e.target.closest("[data-lc-pill]");
    if (pill) { setOpen(open === pill.dataset.lcPill ? null : pill.dataset.lcPill); return; }
    const tag = e.target.closest(".lc-tag");
    if (tag) { toggle(tag.dataset.g, tag.dataset.v, false); return; }
    if (e.target.closest("[data-lc-clear]")) { clear(); return; }
    if (e.target.closest("[data-lc-open]")) { sheet(true); return; }
    if (e.target.closest("[data-lc-close]")) sheet(false);
  });
  document.addEventListener("click", (e) => { if (open && !e.target.closest(".lc-f")) setOpen(null); });
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (open) { const k = open; setOpen(null); $(`[data-lc-pill="${k}"]`)?.focus(); }
    sheet(false);
  });

  const sheetEl = $("[data-lc-sheet]");
  const panel = sheetEl.querySelector(".lc-sheet-p");
  const sheet = (on) => {
    if (on === !sheetEl.hidden && !sheetEl.classList.contains("closing")) return;
    if (on) {
      sheetEl.classList.remove("closing");
      panel.style.transform = "";
      sheetEl.hidden = false;
      document.documentElement.classList.add("lc-lock");
      $(".lc-x", sheetEl).focus();
      return;
    }
    if (sheetEl.hidden) return;
    sheetEl.classList.add("closing");
    panel.style.transform = "translateY(105%)";
    const done = () => { sheetEl.hidden = true; sheetEl.classList.remove("closing"); };
    matchMedia("(prefers-reduced-motion: reduce)").matches ? done() : setTimeout(done, 240);
    document.documentElement.classList.remove("lc-lock");
    openBtn?.focus({ preventScroll: true });
  };
  /* Swipe the handle or the title down to close; a short drag springs back. */
  let y0 = null, dy = 0;
  sheetEl.addEventListener("pointerdown", (e) => {
    if (!e.target.closest("[data-lc-grab]") || e.target.closest("button")) return;
    y0 = e.clientY; dy = 0; sheetEl.classList.add("dragging"); try { panel.setPointerCapture(e.pointerId); } catch {}
  });
  sheetEl.addEventListener("pointermove", (e) => {
    if (y0 === null) return;
    dy = Math.max(0, e.clientY - y0);
    panel.style.transform = `translateY(${dy}px)`;
  });
  const endDrag = () => {
    if (y0 === null) return;
    y0 = null; sheetEl.classList.remove("dragging");
    if (dy > Math.min(140, panel.offsetHeight * 0.25)) sheet(false); else panel.style.transform = "";
  };
  sheetEl.addEventListener("pointerup", endDrag);
  sheetEl.addEventListener("pointercancel", endDrag);

  /* Sorting; on the search page the first order is the best match. */
  const sortSels = $$("[data-lc-sort]");
  let sortBy = "i";
  const sorters = {
    /* Featured: lights first, then tracks, drivers and accessories, spare parts last; within each, products with a photo
       before those still waiting for one; then the collection's order (sections/luz-collection.liquid renders the same). */
    i: searchMode ? (a, b) => a.rank - b.rank : (a, b) => a.part - b.part || (b.vals.cct?.size ? 1 : 0) - (a.vals.cct?.size ? 1 : 0) || a.noimg - b.noimg || a.i - b.i,
    name: (a, b) => a.name.localeCompare(b.name, "en", { numeric: true }),
    code: (a, b) => a.code.localeCompare(b.code, "en", { numeric: true }),
    "w-asc": (a, b) => (a.w || 1e6) - (b.w || 1e6) || a.i - b.i,
    "w-desc": (a, b) => b.w - a.w || a.i - b.i,
  };
  const sort = () => {
    order = [...cards].sort(sorters[sortBy] || sorters.i);
    order.forEach((c) => grid.appendChild(c.el));
    show();
    const u = new URLSearchParams(location.search);
    sortBy === "i" ? u.delete("sort") : u.set("sort", sortBy);
    const s = u.toString();
    history.replaceState(null, "", location.pathname + (s ? `?${s}` : ""));
  };
  /* With the sidebar the sort sits at the end of the category tabs, below that width in the bar (request 120): two
     controls rendered with the page, one value. */
  if (sorters[params.get("sort")]) sortBy = params.get("sort");
  sortSels.forEach((s) => {
    s.value = sortBy;
    s.addEventListener("change", () => { sortBy = s.value; sortSels.forEach((o) => (o.value = sortBy)); reset(); sort(); keep(); });
  });

  if (qInput) {
    let t = 0;
    qInput.addEventListener("input", () => {
      clearTimeout(t);
      t = setTimeout(() => { q = qInput.value.trim(); runQuery(); reset(); render(); sort(); floatPlace?.(); }, 60);
    });
    qInput.form?.addEventListener("submit", (e) => { e.preventDefault(); qInput.blur(); });
  }

  /* Phones: the categories scroll sideways in one row; the edges fade where more pills are hidden and the current one
     starts in view (request 127). On wider screens the pills wrap and nothing scrolls. */
  const cats = $(".lc-cats");
  if (cats) {
    const edge = () => {
      const more = cats.scrollWidth - cats.clientWidth > 2;
      cats.classList.toggle("fade-l", more && cats.scrollLeft > 2);
      cats.classList.toggle("fade-r", more && cats.scrollLeft + cats.clientWidth < cats.scrollWidth - 2);
    };
    const on = $(".lc-cat.on", cats);
    if (on && cats.scrollWidth > cats.clientWidth) {
      const x = on.getBoundingClientRect().left - cats.getBoundingClientRect().left + cats.scrollLeft;
      cats.scrollLeft = x - (cats.clientWidth - on.offsetWidth) / 2;
    }
    cats.addEventListener("scroll", edge, { passive: true });
    addEventListener("resize", edge);
    edge();
  }
  /* The Filters and Sort bar takes the menu's place: the menu slides away as the bar meets it and comes back once the
     bar's place is below it again (request 130, as the product section bar). Category pages below the sidebar width,
     the search page at every width. */
  const fbar = $("[data-lc-bar][data-lc-float]");
  let floatPlace = null;
  if (fbar) {
    const mark = document.createElement("div");
    mark.className = "lc-bar-mark";
    mark.setAttribute("aria-hidden", "true");
    fbar.before(mark);
    const narrow = matchMedia("(max-width: 1180px)");
    let tick = false;
    const place = () => {
      tick = false;
      const on = (fbar.dataset.lcFloat === "all" || narrow.matches) && !!fbar.offsetParent;
      const y = mark.getBoundingClientRect().top;
      const head = document.querySelector(".lhd-wrap")?.offsetHeight || 0;
      document.documentElement.classList.toggle("lhd-away", on && y <= head);
      fbar.classList.toggle("is-stuck", on && y <= parseFloat(getComputedStyle(fbar).top) + 1);
    };
    addEventListener("scroll", () => { if (!tick) { tick = true; requestAnimationFrame(place); } }, { passive: true });
    addEventListener("resize", place);
    floatPlace = place;
    requestAnimationFrame(place);
  }
  if (!groups.length) $("[data-lc-bar]").classList.add("lc-bar--plain");
  reset();
  runQuery();
  render();
  sort();
  root.classList.add("lc-ready");

  /* Coming back to this list lands where it was scrolled, with as many products shown as when it was left. */
  const scrollKey = () => `lc-scroll:${location.pathname}${location.search}`;
  addEventListener("pagehide", () => { try { sessionStorage.setItem(scrollKey(), `${scrollY},${limit},${auto}`); } catch {} });
  try {
    const [y, n, a] = (sessionStorage.getItem(scrollKey()) || "").split(",").map(Number);
    if (y > 0 && performance.getEntriesByType("navigation")[0]?.type === "back_forward") {
      if (a === Infinity) { endless = true; auto = Infinity; }
      if (n > limit) { limit = n; auto = Math.max(auto, n); show(); }
      requestAnimationFrame(() => scrollTo(0, y));
    }
  } catch {}
})();
